import React from 'react';
import { formatPriceFR, formatNumberFR } from '../../utils/formatFR';
import { COULEUR_PORTEFEUILLE } from './couleurs';

const Carte = ({ titre, valeur, detail, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 min-w-0" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{titre}</p>
    <p className="text-xl md:text-2xl font-bold text-slate-900 num mt-1 break-words">{valeur}</p>
    {detail && <p className="text-xs text-slate-500 mt-0.5 break-words">{detail}</p>}
  </div>
);

/** La couleur porte l'identité (carré orange), jamais le texte (lot DI2 § 4). */
const LignePortefeuille = ({ texte, testid }) => (
  <p className="text-xs font-semibold mt-2 text-slate-700 flex items-start gap-1.5"
     data-testid={testid}>
    <span className="inline-block w-2.5 h-2.5 rounded-sm mt-0.5 shrink-0"
          style={{ backgroundColor: COULEUR_PORTEFEUILLE }}></span>
    <span className="break-words">{texte}</span>
  </p>
);

/** Quatre chiffres clés + la 2ᵉ ligne orange du portefeuille (lot DI § 5). */
export const ChiffresCles = ({ donnees, portefeuille, activiteChoisie }) => {
  const k = donnees.kpis || {};
  const premiere = k.premiere_sous_zone || k.premiere_activite || null;
  const p = portefeuille;

  const ecart = (() => {
    if (!p || !activiteChoisie || !p.mediane_demandee || !k.mediane) return null;
    const valeur = Math.round((100 * (p.mediane_demandee - k.mediane)) / k.mediane);
    return `${valeur > 0 ? '+' : ''}${valeur} %`;
  })();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="observatoire-kpis">
      <div>
        <Carte
          titre="Ventes"
          valeur={formatNumberFR(k.ventes || 0)}
          detail={`publiées en ${donnees.mois} mois`}
          testid="observatoire-kpi-ventes"
        />
        {p && (
          <LignePortefeuille
            testid="observatoire-kpi-ventes-portefeuille"
            texte={`Vos fonds en vente : ${formatNumberFR(p.nombre || 0)}`}
          />
        )}
      </div>

      <div>
        <Carte
          titre="Prix médian"
          valeur={k.mediane ? formatPriceFR(k.mediane) : '—'}
          detail={k.insuffisant ? 'pas assez de ventes' : 'moitié au-dessus, moitié en dessous'}
          testid="observatoire-kpi-mediane"
        />
        {p && (
          <LignePortefeuille
            testid="observatoire-kpi-mediane-portefeuille"
            texte={
              !activiteChoisie
                ? 'Vos prix : choisissez une activité'
                : p.mediane_demandee
                  ? `Votre prix demandé : ${formatPriceFR(p.mediane_demandee)}${ecart ? ` (${ecart})` : ''}`
                  : 'Votre prix demandé : moins de 10 biens'
            }
          />
        )}
      </div>

      <div>
        <Carte
          titre="La moitié des ventes entre"
          valeur={k.p25 && k.p75 ? `${formatPriceFR(k.p25)} – ${formatPriceFR(k.p75)}` : '—'}
          detail="1er et 3e quartile"
          testid="observatoire-kpi-quartiles"
        />
        {p && (
          <LignePortefeuille
            testid="observatoire-kpi-quartiles-portefeuille"
            texte={
              !activiteChoisie
                ? 'Vos prix : choisissez une activité'
                : p.p25 && p.p75
                  ? `Vos biens : ${formatPriceFR(p.p25)} – ${formatPriceFR(p.p75)}`
                  : 'Vos biens : moins de 10 biens'
            }
          />
        )}
      </div>

      <Carte
        titre={activiteChoisie ? '1re sous-zone' : '1re activité'}
        valeur={premiere ? premiere.libelle : '—'}
        detail={
          premiere
            ? `${formatNumberFR(premiere.nombre)} ventes${premiere.mediane ? ` · ${formatPriceFR(premiere.mediane)}` : ''}`
            : null
        }
        testid="observatoire-kpi-premiere"
      />
    </div>
  );
};

export default ChiffresCles;
